import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, compressStateChips, itemVisualDensity, laneMicroLabelPlateClass, laneMicroLabelTextClass, productTone, readNumberMeta, readProjectionCouplingLabel, readStringMeta, visibleLaneItems } from "./rendering";

function stateChip(label: string) {
  return <span className="rounded-full border border-amber-300/18 bg-amber-300/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-amber-50/86">{label}</span>;
}

function readProjectionCounts(lane: LaneRendererProps["lane"]) {
  const projectionHealth = lane.metadata?.projectionHealth;
  const byStatus = projectionHealth && typeof projectionHealth === "object" && "byStatus" in projectionHealth && projectionHealth.byStatus && typeof projectionHealth.byStatus === "object"
    ? projectionHealth.byStatus as Record<string, unknown>
    : {};

  const readCount = (key: string) => {
    const value = byStatus[key];
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  };

  return {
    stale: readCount("stale"),
    invalid: readCount("invalid"),
    reviewRequired: readCount("review_required"),
  };
}

function awaitingLabel(authoredFxNodeCount: number, items: LaneRendererProps["items"], visibleItemCount: number) {
  if (items.length > 0 && visibleItemCount === 0) return "outside window";
  if (authoredFxNodeCount > 0) return "awaiting haptic projection";
  return "awaiting authored fx";
}

export default function ProjectionHapticLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);
  const authoredFxNodeCount = readNumberMeta({ ...lane, metadata: lane.metadata } as never, "authoredFxNodeCount") ?? 0;
  const anchoredRangeLabel = readStringMeta({ ...lane, metadata: lane.metadata } as never, "anchoredRangeLabel");
  const projectionCounts = readProjectionCounts(lane);
  const emptyState = awaitingLabel(authoredFxNodeCount, items, visibleItems.length);
  const laneStateChips = [
    projectionCounts.stale > 0 ? `${projectionCounts.stale} stale` : null,
    projectionCounts.invalid > 0 ? `${projectionCounts.invalid} invalid` : null,
    projectionCounts.reviewRequired > 0 ? `${projectionCounts.reviewRequired} review` : null,
    anchoredRangeLabel,
  ].filter((value): value is string => Boolean(value));
  const primaryCouplingLabel = readProjectionCouplingLabel(lane.metadata ?? null, lane.id, viewport.lodTier);
  const compressedLaneState = compressStateChips(laneStateChips, viewport.lodTier);
  const showBodyReason = viewport.lodTier !== "far";

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="projection_haptic" data-lane-id={lane.id}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-amber-200/10" />
      {visibleItems.length > 0 ? (
        <>
          {visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const label = "label" in item
            ? item.label
            : item.itemType === "event"
              ? item.eventKey
              : item.itemType === "overlay"
                ? item.overlayKey
                : item.id;
          const intensity = readNumberMeta(item, "intensity") ?? readNumberMeta(item, "strength") ?? 0.4;
          const pattern = readStringMeta(item, "pattern") ?? readStringMeta(item, "mode");
          const showNearDetail = viewport.lodTier === "near" && density === "regular";
          const showCompactDetail = viewport.lodTier === "mid" || density === "compact";

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-amber-300/24 bg-[linear-gradient(135deg,rgba(251,191,36,0.18),rgba(180,83,9,0.16),rgba(2,6,23,0.20))] px-3 shadow-[0_8px_20px_rgba(245,158,11,0.10)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {showNearDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-amber-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {pattern ? stateChip(pattern) : null}
                        {stateChip(`${Math.round(intensity * 100)}% pulse`)}
                        {compressedLaneState.visible.map((label) => (
                          <React.Fragment key={label}>{stateChip(label)}</React.Fragment>
                        ))}
                        {compressedLaneState.hiddenCount > 0 ? stateChip(`+${compressedLaneState.hiddenCount} more`) : null}
                      </div>
                    </>
                  ) : showCompactDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-amber-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                        {pattern ? stateChip(pattern) : null}
                        {compressedLaneState.visible.map((label) => (
                          <React.Fragment key={label}>{stateChip(label)}</React.Fragment>
                        ))}
                        {compressedLaneState.hiddenCount > 0 ? stateChip(`+${compressedLaneState.hiddenCount} more`) : null}
                      </div>
                    </>
                  ) : (
                    <div className={["inline-flex items-center gap-1.5 rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.14em]", laneMicroLabelPlateClass(lane.productType), laneMicroLabelTextClass(lane.productType)].join(" ")}>
                      <span>HAP</span>
                      <span>{Math.round(intensity * 100)}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </>
      ) : (
        <div className={["min-h-[56px] rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
            <span>
              {items.length > 0
                ? "Projection haptics are outside the active window."
                : authoredFxNodeCount > 0
                  ? "Projection haptics are waiting on derived pulse spans."
                  : "Projection haptics have no derived pulse regions yet."}
            </span>
            {stateChip(emptyState)}
            {compressStateChips([
              projectionCounts.stale > 0 ? `${projectionCounts.stale} stale` : null,
              projectionCounts.invalid > 0 ? `${projectionCounts.invalid} invalid` : null,
              projectionCounts.reviewRequired > 0 ? `${projectionCounts.reviewRequired} review` : null,
              anchoredRangeLabel,
            ].filter((value): value is string => Boolean(value)), viewport.lodTier).visible.map((label) => (
              <React.Fragment key={label}>{stateChip(label)}</React.Fragment>
            ))}
            {primaryCouplingLabel ? stateChip(primaryCouplingLabel) : null}
          </div>
          {showBodyReason ? (
          <div className={["mt-1 text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
            {syncBadge?.reason ?? "Read-only haptic pulse spans will appear here when projection lane items are available."}
          </div>
          ) : null}
        </div>
      )}
    </div>
  );
}